import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, ShieldAlert } from 'lucide-react';
import type { ConsistencyFinding, ConsistencyReport } from '../../services/deliberationApi';

/** WS-3 consistency gate surfacing — renders the factual contradictions the gate
 * found between the seats and the parsed record (endorsement schedule, document
 * inventory, engine outputs). Strategic divergence between the seats is the
 * adversarial product and never appears here. Silent when the gate hasn't run;
 * a slim verified line when it ran clean. */

const SEVERITY_COLOR: Record<ConsistencyFinding['severity'], string> = {
  blocker: 'var(--color-status-adverse)',
  warning: 'var(--color-brand)',
  info: 'var(--color-text-dim)',
};

function FindingRow({ finding }: { finding: ConsistencyFinding }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-2" style={{ borderTop: '1px solid var(--color-border-faint)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-start gap-2 text-left"
        style={{ background: 'transparent' }}
      >
        {open
          ? <ChevronDown className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--color-text-dim)' }} />
          : <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--color-text-dim)' }} />}
        <span
          className="mt-1.5 h-2 w-2 rounded-full shrink-0"
          style={{ background: SEVERITY_COLOR[finding.severity] ?? 'var(--color-text-dim)' }}
          title={finding.severity}
        />
        <span className="cs-body" style={{ color: 'var(--color-text-primary)' }}>
          <span className="cs-label" style={{ color: 'var(--color-text-label)', marginRight: 8 }}>
            {finding.fact_key}
          </span>
          {finding.detail}
        </span>
      </button>
      {open && (
        <div className="ml-8 mt-2 space-y-1">
          <div className="cs-meta" style={{ color: 'var(--color-text-secondary)' }}>
            Seats disagreeing: {finding.seats_disagreeing.join(', ') || '—'}
          </div>
          <pre
            className="cs-meta rounded p-2 overflow-x-auto"
            style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
          >
            {JSON.stringify({ source: finding.source_value, seats: finding.seat_values }, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function ConsistencyBanner({
  report,
  status,
}: {
  report?: ConsistencyReport | null;
  status?: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!report) return null;

  const factual = (report.findings || []).filter(f => f.verdict === 'FACTUAL');
  const blocked = status === 'blocked_consistency';

  if (factual.length === 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 rounded-lg"
        style={{ background: 'transparent', border: '1px solid var(--color-border)' }}>
        <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: 'var(--color-verified)' }} />
        <span className="cs-meta">
          Fact-consistency verified — no seat contradicts the parsed record.
        </span>
      </div>
    );
  }

  const accent = blocked ? 'var(--color-status-adverse)' : 'var(--color-brand)';
  return (
    <div className="rounded-lg px-4 py-3"
      style={{ background: 'var(--color-bg-surface)', border: `1px solid ${accent}` }}>
      <button onClick={() => setExpanded(e => !e)} className="w-full flex items-center gap-3 text-left">
        {blocked
          ? <ShieldAlert className="h-5 w-5 shrink-0" style={{ color: accent }} />
          : <AlertTriangle className="h-5 w-5 shrink-0" style={{ color: accent }} />}
        <div className="flex-1">
          <span className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>
            {factual.length} factual {factual.length === 1 ? 'contradiction' : 'contradictions'} against the parsed record
          </span>
          <span className="cs-meta ml-2" style={{ color: 'var(--color-text-secondary)' }}>
            {blocked
              ? 'Finalize is blocked until resolved (consistency gate: enforcing).'
              : `Advisory — review before relying on these figures${report.mode ? ` (mode: ${report.mode})` : ''}.`}
          </span>
        </div>
        {expanded
          ? <ChevronDown className="h-4 w-4 shrink-0" style={{ color: 'var(--color-text-dim)' }} />
          : <ChevronRight className="h-4 w-4 shrink-0" style={{ color: 'var(--color-text-dim)' }} />}
      </button>
      {expanded && (
        <div className="mt-2">
          {factual.map((f, i) => <FindingRow key={`${f.fact_key}-${i}`} finding={f} />)}
          {report.strategic_note && (
            <p className="cs-meta mt-2" style={{ color: 'var(--color-text-dim)' }}>
              {report.strategic_note}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
